import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_HIRSCHBACH_APP, EnableHirschbachAppInput } from '../data/mutations'

type FieldValues = { apiKey: string; apiURL: string }

const Footer = ({ formError }: { formError: string }) => (
  <Box>
    <Text color="red.600">{formError}</Text>
    <Text>
      {`See `}
      <Link
        color="blue.600"
        href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/hirschbach"
        target="_blank"
        rel="noopener noreferrer"
      >
        detailed steps here.
      </Link>
    </Text>
  </Box>
)

/**
 * Saves Hirschbach app's api key, api URL
 *
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const HirschbachModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { showToast } = Toast()
  const [formError, setFormError] = useState('')

  const [enableHirschbachApp, { loading }] = useMutation<
    { enableHirschbachApp: { apiKey: string; apiURL: string } },
    EnableHirschbachAppInput
  >(ENABLE_HIRSCHBACH_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `Hirschbach app is enabled successfully!`
      })
      refetchApps()
      onClose()
      setFormError('')
    },
    onError: (err) => {
      if (err.message === 'Your API key or URL is invalid.') {
        setFormError('Invalid api key or URL. Please try again.')
        showToast({
          status: 'error',
          title: `Invalid api key or URL. Please try again.`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    enableHirschbachApp({
      variables: {
        apiKey: event.apiKey,
        apiURL: event.apiURL,
        appType
      }
    })
  }

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable ${appType} for Chaine`}
        subtitle={`Enabling this integration requires an admin Hirschbach account. Enter your Hirschbach API Key, API URL`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        footer={formError ? <Footer formError={formError} /> : <></>}
        modalItems={[
          {
            labelText: 'Hirschbach Api Key',
            name: 'apiKey',

            placeHolder: 'Enter your Hirschbach Api Key',
            type: 'input'
          },
          {
            labelText: 'Hirschbach API URL',
            name: 'apiURL',

            placeHolder: 'Enter your Hirschbach API URL',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

HirschbachModal.displayName = 'HirschbachModal'

export { HirschbachModal }
